// Favoris « invité » (sans compte) : stockés en localStorage pour que l'Agenda
// fonctionne sans connexion. À la connexion, ils sont fusionnés côté serveur
// par mergeGuestFavorites (appelée depuis AuthProvider).
import { supabase } from '@/lib/supabase';

const STORAGE_KEY = 'lafete:guest-favorites';

export const FAVORITES_CHANGED_EVENT = 'lafete:favorites-changed';

export function readGuestFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function writeGuestFavorites(ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch { /* quota / disabled storage — ignore */ }
}

/**
 * Fusionne les favoris invités dans le compte : insère les ids locaux dans
 * `favorites` (en ignorant les doublons), puis vide le stockage local.
 * Les événements purgés entre-temps sont écartés pour ne pas violer la FK.
 * Idempotent : sans favoris locaux, ne fait rien.
 */
export async function mergeGuestFavorites(userId: string): Promise<number> {
  const ids = readGuestFavorites();
  if (!ids.length) return 0;

  const { data: existing, error: evError } = await supabase
    .from('events').select('id').in('id', ids);
  if (evError) return 0; // hors-ligne / erreur : on retentera au prochain démarrage

  const valid = (existing ?? []).map((r: { id: string }) => r.id);
  if (valid.length) {
    const { error } = await supabase
      .from('favorites')
      .upsert(valid.map((event_id) => ({ user_id: userId, event_id })), {
        onConflict: 'user_id,event_id',
        ignoreDuplicates: true,
      });
    if (error) return 0;
  }

  writeGuestFavorites([]);
  window.dispatchEvent(new CustomEvent(FAVORITES_CHANGED_EVENT));
  return valid.length;
}
